import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Check, ChevronUp } from 'lucide-react';

interface RepeaterItemSummary {
  title: string;
  subtitle?: string;
}

interface RepeaterProps<T> {
  title: string;
  description?: string;
  items: T[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  addButtonText?: string;
  getItemSummary?: (item: T) => RepeaterItemSummary;
}

function RepeaterItem<T>({ item, index, renderItem, onRemove, getItemSummary }: {
  item: T;
  index: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  onRemove: (index: number) => void;
  getItemSummary?: (item: T) => RepeaterItemSummary;
}) {
  // Start expanded so the user can edit immediately when adding
  const [isExpanded, setIsExpanded] = useState(true); 

  const summary = getItemSummary ? getItemSummary(item) : null;
  const showSummary = summary && !isExpanded;

  return (
    <div className={`relative group border rounded-xl bg-white transition-all duration-200 ${isExpanded ? 'border-indigo-200 ring-1 ring-indigo-50/50 shadow-md' : 'border-gray-200 hover:border-gray-300 shadow-sm'}`}>
      
      {showSummary ? (
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 rounded-xl"
          onClick={() => setIsExpanded(true)}
        >
          <div className="flex-1 min-w-0 pr-4">
            <h4 className="text-sm font-semibold text-gray-900 truncate">{summary.title || `Item ${index + 1}`}</h4>
            {summary.subtitle && <p className="text-xs text-gray-500 truncate mt-0.5">{summary.subtitle}</p>}
          </div>

          <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
             <button 
               type="button" 
               className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
               onClick={(e) => { e.stopPropagation(); setIsExpanded(true); }}
               title="Editar"
             >
               <Edit2 size={16} />
             </button>
             <button 
               type="button" 
               className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
               onClick={(e) => { e.stopPropagation(); onRemove(index); }}
               title="Remover"
             >
               <Trash2 size={16} />
             </button>
          </div>
        </div>
      ) : (
        <div className="p-5 animate-in fade-in slide-in-from-top-2">
            <div className="absolute right-3 top-3 flex gap-1 z-10 transition-opacity">
               {getItemSummary && (
                <button
                   onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }}
                   type="button"
                   className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors focus:outline-none"
                   title="Ocultar"
                 >
                   <ChevronUp size={16} />
                 </button>
               )}
               <button
                  onClick={() => onRemove(index)}
                  type="button"
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors focus:outline-none"
                  title="Remover"
                >
                  <Trash2 size={16} />
                </button>
            </div>
            
            <div className="pt-2">
               {renderItem(item, index)}
            </div>

            {getItemSummary && (
              <div className="mt-5 pt-4 border-t border-gray-100 flex justify-end">
                 <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                 >
                    <Check size={16} />
                    Salvar Item
                 </button>
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export function Repeater<T>({ title, description, items, onAdd, onRemove, renderItem, addButtonText = "Adicionar", getItemSummary }: RepeaterProps<T>) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-gray-200 pb-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
        <button
          onClick={onAdd}
          type="button"
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:z-10 focus:ring-2 focus:ring-gray-300 transition-colors shadow-sm"
        >
          <Plus size={16} />
          {addButtonText}
        </button>
      </div>

      <div className="space-y-4 pt-1">
        {items.map((item, index) => (
          <RepeaterItem
            key={index}
            item={item}
            index={index}
            renderItem={renderItem}
            onRemove={onRemove}
            getItemSummary={getItemSummary}
          />
        ))}
        {items.length === 0 && (
          <div className="text-center py-8 px-4 border text-sm border-dashed border-gray-300 rounded-xl bg-gray-50/50">
            <p className="text-gray-500">Nenhum item adicionado ainda.</p>
          </div>
        )}
      </div>
    </div>
  );
}
